import { z } from "zod";
import { TimeboxLength } from "./definitions.js";
const Id = z.number().int().positive();
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD");
export const TimeboxStatus = z.enum(["planned", "active", "closed"]);
/** A period in a container at one of its definition's timebox levels (design 3.7). */
export const Timebox = z.object({
    id: Id,
    containerId: Id,
    levelId: Id,
    parentId: Id.nullable(),
    name: z.string(),
    sequence: z.number().int(),
    startDate: IsoDate.nullable(),
    endDate: IsoDate.nullable(),
    /** templates: days from the project's start, and length in days (kept on copies) */
    offsetDays: z.number().int().nullable(),
    lengthDays: z.number().int().nullable(),
    status: TimeboxStatus,
    /** false once someone set the status by hand; the worker then leaves it alone */
    autoStatus: z.boolean(),
    /** the cadence that made it, if any */
    cadenceId: Id.nullable(),
    deletedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CreateTimeboxInput = z.object({
    /** level id, or its key */
    levelId: Id.optional(),
    level: z.string().optional(),
    parentId: Id.nullable().default(null),
    name: z.string().min(1).max(200),
    sequence: z.number().int().optional(),
    startDate: IsoDate.nullable().optional(),
    endDate: IsoDate.nullable().optional(),
    /** templates only, instead of dates: days after the project's start, and length in days */
    offsetDays: z.number().int().min(0).max(36_500).nullable().optional(),
    lengthDays: z.number().int().min(1).max(3_660).nullable().optional(),
    status: TimeboxStatus.default("planned"),
});
export const UpdateTimeboxInput = z
    .object({
    name: z.string().min(1).max(200).optional(),
    parentId: Id.nullable().optional(),
    sequence: z.number().int().optional(),
    startDate: IsoDate.nullable().optional(),
    endDate: IsoDate.nullable().optional(),
    offsetDays: z.number().int().min(0).max(36_500).nullable().optional(),
    lengthDays: z.number().int().min(1).max(3_660).nullable().optional(),
    status: TimeboxStatus.optional(),
})
    .strict();
export const ListTimeboxesQuery = z.object({
    /** level key */
    level: z.string().optional(),
    status: TimeboxStatus.optional(),
    /** periods ending on or after this date */
    from: IsoDate.optional(),
    /** periods starting on or before this date */
    to: IsoDate.optional(),
    include_deleted: z.enum(["true", "false"]).optional(),
});
export const DeleteTimeboxQuery = z.object({ unallocate: z.enum(["true", "false"]).optional() });
export const CloseTimeboxInput = z.object({
    /** move unfinished items to the next period; defaults to the level's setting */
    carryOver: z.boolean().optional(),
});
export const CloseTimeboxResult = z.object({ timebox: Timebox, carriedOver: z.array(Id), to: Id.nullable() });
/** Periods made on a schedule, for projects with no end (design 3.7). */
export const Cadence = z.object({
    id: Id,
    containerId: Id,
    levelId: Id,
    length: TimeboxLength,
    anchorDate: IsoDate.nullable(),
    /** templates: the first period's start in days after the project's start */
    anchorOffsetDays: z.number().int().nullable(),
    namePattern: z.string(),
    horizon: z.number().int(),
    nextSequence: z.number().int(),
    active: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/** Name tokens: {n} running number, {yyyy} {q} {mm} {ww} from the start date, {parent} the parent period's name. */
const NamePattern = z.string().min(1).max(120);
export const CreateCadenceInput = z.object({
    levelId: Id.optional(),
    level: z.string().optional(),
    /** defaults to the level's default length */
    length: TimeboxLength.optional(),
    /** first period's start; required at the top level, optional below (periods then start with their parent) */
    anchorDate: IsoDate.optional(),
    /** templates only, instead of anchorDate */
    anchorOffsetDays: z.number().int().min(0).max(36_500).optional(),
    namePattern: NamePattern.optional(),
    horizon: z.number().int().min(1).max(52).default(3),
});
export const UpdateCadenceInput = z
    .object({ namePattern: NamePattern.optional(), horizon: z.number().int().min(1).max(52).optional(), active: z.boolean().optional() })
    .strict();
//# sourceMappingURL=timeboxes.js.map